import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create an axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Sends a chat prompt to the backend
 * @param {string} prompt - User's natural language query
 * @returns {Promise<Object>} - Response data
 */
export const sendChatPrompt = async (prompt) => {
  try {
    const response = await api.post('/prompt', { prompt });
    return response.data;
  } catch (error) {
    console.error('Error sending chat prompt:', error);
    throw error;
  }
};

/**
 * Gets wallet balance
 * @param {string} address - Wallet address
 * @param {string} chain - Blockchain chain ID
 * @returns {Promise<Object>} - Wallet balance data
 */
export const getWalletBalance = async (address, chain = '0x1') => {
  try {
    const response = await api.get(`/wallet/${address}/balance/${chain}`);
    return response.data;
  } catch (error) {
    console.error('Error getting wallet balance:', error);
    throw error;
  }
};

/**
 * Gets wallet transaction history
 * @param {string} address - Wallet address
 * @param {string} chain - Blockchain chain ID
 * @param {number} limit - Maximum number of transactions to return
 * @returns {Promise<Array>} - Transaction history
 */
export const getTransactionHistory = async (address, chain = '0x1', limit = 50) => {
  try {
    const response = await api.get(`/wallet/${address}/history/${chain}?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error getting transaction history:', error);
    throw error;
  }
};

/**
 * Gets token information
 * @param {string} address - Token contract address
 * @param {string} chain - Blockchain chain ID
 * @returns {Promise<Object>} - Token information
 */
export const getTokenInfo = async (address, chain = '0x1') => {
  try {
    const response = await api.get(`/token/${address}/info/${chain}`);
    return response.data;
  } catch (error) {
    console.error('Error getting token info:', error);
    throw error;
  }
};

/**
 * Gets transaction details
 * @param {string} hash - Transaction hash
 * @param {string} chain - Blockchain chain ID
 * @returns {Promise<Object>} - Transaction details
 */
export const getTransactionDetails = async (hash, chain = '0x1') => {
  try {
    const response = await api.get(`/transaction/${hash}/details/${chain}`);
    return response.data;
  } catch (error) {
    console.error('Error getting transaction details:', error);
    throw error;
  }
};

export default api;
